import React, { useMemo } from "react";
import {
  FaUserFriends,
  FaChalkboardTeacher,
  FaCogs,
  FaCheckCircle,
  FaExternalLinkAlt,
} from "react-icons/fa";

const cx = (...c) => c.filter(Boolean).join(" ");

const RoleCard = ({ icon: Icon, title, items, accent }) => (
  <div
    className={cx(
      "rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg",
      "dark:border-white/10 dark:bg-slate-900"
    )}
  >
    <div className="flex items-start gap-3">
      <div
        className={cx(
          "grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-white",
          "bg-gradient-to-r",
          accent
        )}
      >
        <Icon />
      </div>

      <div>
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </div>
        <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Fonctionnalités clés selon le profil.
        </div>
      </div>
    </div>

    <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-200">
      {items.map((t) => (
        <li key={t} className="flex gap-2">
          <FaCheckCircle className="mt-0.5 text-emerald-500" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AboutMonAppliClub = ({ onDemoClick }) => {
  const roles = useMemo(
    () => [
      {
        icon: FaUserFriends,
        title: "Adhérents",
        accent: "from-yellow-400 to-orange-500",
        items: [
          "Inscription en ligne + dépôt des documents",
          "Paiement en ligne des cotisations",
          "Calendrier, actualités et messagerie",
          "Boutique, galerie et contenus pédagogiques",
        ],
      },
      {
        icon: FaChalkboardTeacher,
        title: "Enseignants",
        accent: "from-sky-500 to-indigo-600",
        items: [
          "Listing adhérents et appel de présence",
          "Passages de grades et ceintures",
          "Suivi compétitions et palmarès",
          "Gestion d’événements et ToDoList",
        ],
      },
      {
        icon: FaCogs,
        title: "Bureau / Dirigeants",
        accent: "from-emerald-500 to-green-600",
        items: [
          "Tableaux de bord et pilotage",
          "Comptabilité et suivi des paiements",
          "Boutique, commandes et stocks",
          "Paramètres et personnalisation du site",
        ],
      },
    ],
    []
  );

  return (
    <section className="bg-white py-16 sm:py-20 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-6 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-7">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Fonctionnalités
              </div>

              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
                Une solution complète pour le quotidien de votre club
              </h2>

              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                MonAppliClub centralise les usages essentiels : gestion des
                adhérents, inscriptions, paiements, communication, suivi sportif
                et outils internes selon les rôles.
              </p>

              <div className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                <img
                  src="/images/illustration_mac_phone.png"
                  alt="MonAppliClub"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="mt-6 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                {[
                  "Application web responsive : PC, tablette et mobile",
                  "Accès différencié selon les profils",
                  "Outils pensés pour l’organisation réelle d’un club",
                ].map((item) => (
                  <div key={item} className="flex gap-3">
                    <FaCheckCircle className="mt-0.5 text-sky-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={onDemoClick}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-sky-600"
              >
                Tester la démo <FaExternalLinkAlt className="text-sm" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 gap-6">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Par profil
              </div>
              <h3 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
                Chaque utilisateur accède aux bons outils au bon moment
              </h3>
              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                L’application s’adapte au fonctionnement du club : adhérents,
                enseignants et dirigeants n’ont pas les mêmes besoins, ni les
                mêmes accès.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {roles.map((r) => (
                <RoleCard
                  key={r.title}
                  icon={r.icon}
                  title={r.title}
                  items={r.items}
                  accent={r.accent}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMonAppliClub;